import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronsUpDown,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  ListFilter,
  Loader2,
  Save,
  Upload,
} from "lucide-react";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import { DraggablePhotoViewer } from "./DraggablePhotoViewer";
import type { OptionsMap, RequiredSet, TemplateField } from "./hooks/useTemplateOptions";
import type { ExcelCellMapping } from "../services/exportSurveyToExcelTemplate";
import { exportSurveyToExcelTemplate } from "../services/exportSurveyToExcelTemplate";
import {
  fetchExportAssets,
  fetchExportProperty,
  fetchPropertyExportTemplateUrl,
} from "../services/fetchExportProperty";
import { generateSurveyExportViaEdge } from "../services/generateSurveyExportViaEdge";
import { fillAnswerGapsFromProdData } from "../services/fillAnswerGapsFromProdData";
import { uploadExportToRIProject } from "../services/uploadExportToRIProject";
import type { ExcelExportChoice, ExportDestination } from "./hooks/useExcelTemplatePicker";
import { useExcelTemplatePicker } from "./hooks/useExcelTemplatePicker";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/shared/ui/dropdown-menu";
import type { AssetsByCategory } from "./hooks/usePropertySurveyAssets";
import { useDownloadManager } from "@/features/exports/services/DownloadManagerContext";
import { buildSurveyPhotosBundle } from "@/features/exports/services/bundles";
import { sanitize, triggerBlobDownload } from "@/features/exports/services/_helpers";

import {
  AssetsByCategoryContext,
  ExpandAllContext,
  HideAnsweredContext,
  HideRequiredContext,
  OptionsMapContext,
  RequiredSetContext,
  SurveyIdContext,
} from "./siteSurveyEditor/contexts";
import { countLeafFields, countSurveyPhotos, extractFieldValues } from "./siteSurveyEditor/surveyHelpers";
import { FieldRenderer } from "./siteSurveyEditor/FieldRenderer";
import { PropertyAssetsSection } from "./siteSurveyEditor/PropertyAssetsSection";

interface SiteSurveyEditorViewProps {
  fields: AttributeField[];
  isDirty: boolean;
  isSaving: boolean;
  projectId: string;
  propertyId: string;
  surveyId: string;
  propertyName?: string | null;
  propertyAddress?: string | null;
  onUpdateField: (refId: string, value: string | number | boolean | string[] | null) => void;
  onUpdateFieldMetadata: (
    fieldId: string,
    patch: { confidence?: number | null; evidence?: string[]; notes?: string | null }
  ) => void;
  onSaveNow: () => void;
  optionsMap?: OptionsMap;
  requiredSet?: RequiredSet;
  assetsByCategory?: AssetsByCategory;
  templateFields?: TemplateField[];
  excelCellMappings?: ExcelCellMapping[];
  /** Template's default worksheet for sheetless mappings (attribute_templates.excel_default_sheet). */
  excelDefaultSheet?: string | null;
  /** The open survey's captured_at, for date mappings in the Excel export. */
  capturedAt?: string | null;
  /** The survey row's prod_data — export-time gap fill for missing answers. */
  prodData?: Record<string, unknown> | null;
}

export function SiteSurveyEditorView({
  fields,
  isDirty,
  isSaving,
  projectId,
  propertyId,
  surveyId,
  propertyName,
  propertyAddress,
  onUpdateField,
  onUpdateFieldMetadata,
  onSaveNow,
  optionsMap = {},
  requiredSet = new Set<string>(),
  assetsByCategory = {},
  templateFields = [],
  excelCellMappings = [],
  excelDefaultSheet = null,
  capturedAt = null,
  prodData = null,
}: SiteSurveyEditorViewProps) {
  const [photoViewer, setPhotoViewer] = useState<{
    urls: string[];
    index: number;
    title: string;
  } | null>(null);

  const { enqueue } = useDownloadManager();

  const assetPhotoCount = useMemo(
    () =>
      Object.values(assetsByCategory).reduce(
        (sum, group) => sum + group.photos.length,
        0,
      ),
    [assetsByCategory],
  );
  const surveyPhotoCount = useMemo(() => countSurveyPhotos(fields), [fields]);
  const totalDownloadablePhotos = assetPhotoCount + surveyPhotoCount;

  const handleDownloadAllPhotos = () => {
    const slug = sanitize(propertyName || "property");
    enqueue({
      label: `Survey photos — ${propertyName || "Property"}`,
      filename: `${slug}_survey_photos.zip`,
      build: (ctx) =>
        buildSurveyPhotosBundle(
          { projectId, propertyId, surveyFields: fields },
          ctx,
        ),
    });
  };

  // Progress/result popup for the export/upload pipeline.
  const [exportStatus, setExportStatus] = useState<{
    phase: "working" | "done" | "error";
    message: string;
  } | null>(null);
  const working = (message: string) => setExportStatus({ phase: "working", message });
  const finished = (message: string) => setExportStatus({ phase: "done", message });
  const failed = (message: string) => setExportStatus({ phase: "error", message });

  const runServerExcelExport = async (choice: ExcelExportChoice) => {
    try {
      working("Generating export on the server…");
      const result = await generateSurveyExportViaEdge({
        propertyId,
        projectId,
        surveyId,
        destination: choice.destination,
        defaultSheet: choice.defaultSheet,
      });
      if (result.kind === "error") {
        const message =
          {
            no_template_url: "This property's template URL is no longer set — pick a template file instead.",
            no_survey: "This property has no survey to export.",
            no_mappings: "This survey's template has no Excel cell mappings (excelTemplateCell).",
            no_answers: "No answers to export — questions are mapped but none are answered.",
            no_ref_id: "This property has no linked RoboticImaging project (ref_id) — cannot upload.",
          }[result.error] ??
          `Server export failed (${result.error})${result.message ? `: ${result.message}` : ""}.`;
        failed(message);
        return;
      }
      if (result.kind === "uploaded") {
        finished(`Uploaded "${result.filename}" to the RoboticImaging project's Photos files.`);
        return;
      }
      triggerBlobDownload(result.blob, result.filename);
      finished(`Exported ${result.written} answer(s) into the template.`);
    } catch (err) {
      console.error("[excel export]", err);
      failed("Server export failed — see the console for details.");
    }
  };

  const runExcelExport = async (file: File, choice: ExcelExportChoice) => {
    try {
      working("Loading property info…");
      // Property info feeds the mappings and [Address]/[Site #] tokens; assets
      // feed the Summary photo slots (full-shot vs nameplate pairs).
      const [property, assets] = await Promise.all([
        fetchExportProperty(propertyId).catch(() => null),
        fetchExportAssets(propertyId).catch(() => []),
      ]);
      if (choice.destination === "upload" && !property?.ref_id) {
        failed("This property has no linked RoboticImaging project (ref_id) — cannot upload.");
        return;
      }
      // Exports ship the best available answers: fill gaps in the resolved
      // tree from prod_data (never overwriting editor values).
      const exportFields = fillAnswerGapsFromProdData(fields, prodData);
      const result = await exportSurveyToExcelTemplate({
        file,
        templateFields,
        fields: exportFields,
        propertyName,
        propertyAddress,
        excelCellMappings,
        mappingContext: { property, capturedAt },
        assets,
        prodData,
        surveyId,
        defaultSheet: choice.defaultSheet ?? excelDefaultSheet,
        deliver: choice.destination === "upload" ? "blob" : "download",
        onProgress: working,
      });
      const { written, mapped } = result;
      if (mapped === 0) {
        failed("This survey's template has no Excel cell mappings (excelTemplateCell).");
        return;
      }
      if (written === 0) {
        failed(`No answers to export — ${mapped} question(s) are mapped but none are answered.`);
        return;
      }
      if (choice.destination === "upload" && result.data && result.filename) {
        working("Uploading to the RoboticImaging project…");
        await uploadExportToRIProject(result.data, result.filename, property!.ref_id!);
        finished(`Uploaded "${result.filename}" to the RoboticImaging project's Photos files.`);
      } else {
        finished(`Exported ${written} answer(s) into the template.`);
      }
    } catch (err) {
      console.error("[excel export]", err);
      failed("Export failed — see the console for details.");
    }
  };

  // The picker dialog remains for the local-file override; the export menu's
  // Download/Upload actions go straight to the server export when a template
  // URL is configured (property first, else project), falling back to the
  // local file flow when none is.
  const { pickLocal: pickLocalTemplate, dialog: excelSheetDialog } = useExcelTemplatePicker(
    (source, choice) => {
      if (source.kind === "local") void runExcelExport(source.file, choice);
    },
    { templateDefaultSheet: excelDefaultSheet }
  );

  const handleServerExport = async (destination: ExportDestination) => {
    const url = await fetchPropertyExportTemplateUrl(propertyId, projectId).catch(() => null);
    if (!url) {
      pickLocalTemplate();
      return;
    }
    await runServerExcelExport({ destination });
  };

  // Expand / Collapse all
  const [expandAll, setExpandAll] = useState({ expanded: false, version: 0 });
  const toggleExpandAll = () =>
    setExpandAll((prev) => ({ expanded: !prev.expanded, version: prev.version + 1 }));

  // Hide answered
  const [hideAnswered, setHideAnswered] = useState(false);

  // Hide field questions (required in the template)
  const [hideRequired, setHideRequired] = useState(false);

  return (
    <OptionsMapContext.Provider value={optionsMap}>
    <RequiredSetContext.Provider value={requiredSet}>
    <AssetsByCategoryContext.Provider value={assetsByCategory}>
    <ExpandAllContext.Provider value={expandAll}>
    <HideAnsweredContext.Provider value={hideAnswered}>
    <HideRequiredContext.Provider value={hideRequired}>
    <SurveyIdContext.Provider value={surveyId}>
    <>
      {/* Toolbar */}
      <div className="sticky top-0 z-40 flex flex-col gap-1.5 py-2 px-1">
        <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={toggleExpandAll}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronsUpDown className="h-3.5 w-3.5" />
            {expandAll.expanded ? "Collapse All" : "Expand All"}
          </button>
          <button
            onClick={() => setHideAnswered((h) => !h)}
            className={cn(
              "flex items-center gap-1.5 text-xs transition-colors",
              hideAnswered
                ? "text-primary hover:text-primary/80"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {hideAnswered ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {hideAnswered ? "Show Answered" : "Hide Answered"}
          </button>
          <button
            onClick={() => setHideRequired((h) => !h)}
            className={cn(
              "flex items-center gap-1.5 text-xs transition-colors",
              hideRequired
                ? "text-primary hover:text-primary/80"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <ListFilter className="h-3.5 w-3.5" />
            {hideRequired ? "Show Field Questions" : "Hide Field Questions"}
          </button>
        </div>

        {/* Progress counter */}
        {(() => {
          const { total, answered } = countLeafFields(fields);
          const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
          return (
            <span className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{answered}</span>
              /{total} answered ({pct}%)
            </span>
          );
        })()}
        </div>

        <div className="flex items-center justify-end gap-3">
          {/* Download all photos */}
          <button
            onClick={handleDownloadAllPhotos}
            disabled={totalDownloadablePhotos === 0}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-muted-foreground"
          >
            <Download className="h-3.5 w-3.5" />
            Download photos
            <span className="text-muted-foreground/70">
              ({assetPhotoCount} assets · {surveyPhotoCount} survey)
            </span>
          </button>

          {/* Export answers into an Excel template */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title="Export answers into the configured Excel template"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Export Excel
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => void handleServerExport("download")}>
                Download
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void handleServerExport("upload")}>
                Upload to RI project
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => pickLocalTemplate()}>
                From a local template file…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Backup / Restore */}
          <button
            onClick={() => {
              const data = extractFieldValues(fields);
              const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `survey-backup-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title="Export answers as JSON backup"
          >
            <Download className="h-3.5 w-3.5" />
            Backup
          </button>
          <button
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".json";
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  try {
                    const data = JSON.parse(ev.target?.result as string);
                    if (typeof data !== "object" || data === null) {
                      alert("Invalid backup file format.");
                      return;
                    }
                    // Collect existing ref_ids
                    const existing = extractFieldValues(fields);
                    let updated = 0;
                    for (const [refId, value] of Object.entries(data)) {
                      if (refId in existing) {
                        onUpdateField(refId, value as any);
                        updated++;
                      }
                    }
                    alert(`Restored ${updated} field(s).`);
                  } catch {
                    alert("Failed to parse backup file.");
                  }
                };
                reader.readAsText(file);
              };
              input.click();
            }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title="Import answers from JSON backup (values only)"
          >
            <Upload className="h-3.5 w-3.5" />
            Restore
          </button>

          {/* Save status */}
          {isSaving && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground animate-fade-in">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </div>
          )}
          {isDirty && !isSaving && (
            <button
              onClick={onSaveNow}
              className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors animate-fade-in"
            >
              <Save className="h-3 w-3" />
              Unsaved changes
            </button>
          )}
          {!isDirty && !isSaving && (
            <span className="text-xs text-muted-foreground/50">All changes saved</span>
          )}
        </div>
      </div>

      {/* Property Assets section */}
      <div className="mb-4">
        <PropertyAssetsSection propertyId={propertyId} />
      </div>

      {/* Fields */}
      <div className="space-y-0.5">
        {[...fields]
          .sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""))
          .map((field) => (
            <FieldRenderer
              key={field.id}
              field={field}
              depth={0}
              propertyId={propertyId}
              onUpdateField={onUpdateField}
              onUpdateFieldMetadata={onUpdateFieldMetadata}
              onOpenPhotos={(urls, index, title) =>
                setPhotoViewer({ urls, index, title })
              }
            />
          ))}
      </div>

      {/* Excel export: target sheet picker */}
      {excelSheetDialog}

      {/* Excel export: progress + result popup */}
      {exportStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex max-w-md flex-col gap-3 rounded-lg border bg-background px-5 py-4 shadow-lg">
            <div className="flex items-center gap-3">
              {exportStatus.phase === "working" && (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
              )}
              {exportStatus.phase === "done" && (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
              )}
              {exportStatus.phase === "error" && (
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
              )}
              <span className="text-sm">{exportStatus.message}</span>
            </div>
            {exportStatus.phase !== "working" && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setExportStatus(null)}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo viewer */}
      {photoViewer && (
        <DraggablePhotoViewer
          photos={photoViewer.urls.map((url, i) => ({ url, name: `Photo ${i + 1}` }))}
          initialIndex={photoViewer.index}
          title={photoViewer.title}
          onClose={() => setPhotoViewer(null)}
        />
      )}
    </>
    </SurveyIdContext.Provider>
    </HideRequiredContext.Provider>
    </HideAnsweredContext.Provider>
    </ExpandAllContext.Provider>
    </AssetsByCategoryContext.Provider>
    </RequiredSetContext.Provider>
    </OptionsMapContext.Provider>
  );
}
